import React from 'react';
import Joi from 'joi';
import JoiPhoneNumber from 'joi-phone-number';

import { connect } from 'react-redux';
import { loadDivisions } from '../../store/divisions';
import { USER_TYPES } from '../../utils/constants';

import Conditional from '../common/conditional';
import Form from '../common/form';
import Progress from '../themed/progress';

import * as userService from '../../services/userService';
import * as toastService from '../../services/toastService';

import * as utils from '../../utils/utilities';
import * as data from '../../utils/data';
import * as models from '../../utils/models';

import withRoles from '../hoc/withRoles';
import withOrganizations from '../hoc/withOrganizations';

import routes from '../../utils/routes';

const countryCode: string = process.env.REACT_APP_COUNTRY_CODE as string;

class CreateUser extends Form {
	state = {
		data: {
			firstName: '',
			lastName: '',
			phone: '',
			email: '',
			designation: '',
			facilityId: '',
			organisationId: '',
			roleId: '',
			division: [],
			userType: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		firstName: Joi.string().min(3).required().label('First Name'),
		lastName: Joi.string().min(3).required().label('Last Name'),
		email: Joi.string()
			.email({ tlds: false })
			.required()
			.label('Email Address'),
		roleId: Joi.string().min(3).required().label('Role'),
		designation: Joi.string().min(3).required().label('Designation'),
		phone: Joi.extend(JoiPhoneNumber)
			.string()
			.allow('')
			.optional()
			.phoneNumber({ defaultCountry: countryCode, strict: true })
			.label('Contact Phone number'),
		userType: Joi.string().min(3).required().label('User Type'),
		division: Joi.any().when('userType', {
			is: USER_TYPES.INTERNAL,
			then: Joi.array()
				.min(0)
				.items(
					Joi.object({
						id: Joi.string().required(),
						name: Joi.string().required(),
					})
				)
				.required()
				.label('Division'),
			otherwise: Joi.optional().label('Division'),
		}),
		organisationId: Joi.any().when('userType', {
			is: 'external user',
			then: Joi.string().required().label('Organization'),
		}),
		facilityId: Joi.any().when('userType', {
			is: 'external user',
			then: Joi.string().required().label('Facility'),
		}),
	};

	componentDidMount() {
		this.props.loadDivisions();
	}

	getOrganizationFacilities = (): models.SelectOption[] => {
		const props: any = this.props;
		const organizations: models.Organization[] = props.organizations;
		const organizationId: string = this.state.data.organisationId;

		const organization = organizations.find((o) => o._id === organizationId);
		if (!organization) return [];

		return organization.facilities.map(utils.transformFacilityItem);
	};

	getApiData = () => {
		const apiData: any = { ...this.state.data };
		const isInternalUser: boolean = apiData.userType === USER_TYPES.INTERNAL;

		if (!apiData.phone) {
			delete apiData.phone;
		}

		if (isInternalUser) {
			apiData.division = apiData.division.map((d: models.SelectOption) => d.id);

			delete apiData.organisationId;
			delete apiData.facilityId;
		} else {
			delete apiData.division;
		}

		return apiData;
	};

	getRoles = (): models.Role[] => {
		const { userType } = this.state.data;
		const { roles } = this.props;

		return roles.filter((r: models.Role) => r.userType === userType);
	};

	postData = async (): Promise<void> => {
		await userService.createUser(this.getApiData(), this.handleProgressChange);
		window.location.href = routes.users.fullPath;
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: 'Creating User',
			success: 'User Created',
			error: utils.getErrorMessage,
		});
	};

	render() {
		const { userType } = this.state.data;

		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit} className="p-3">
					<h1 className="form-title">Create a New User</h1>

					<div className="col">
						{this.renderSelect({
							name: 'userType',
							label: 'User Type',
							placeholder: 'Select User Type',
							options: data.getUserTypes(),
						})}
					</div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'firstName',
								label: 'First Name',
								placeholder: 'e.g. John',
							})}
						</div>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'lastName',
								label: 'Last Name',
								placeholder: 'e.g. Doe',
							})}
						</div>
					</div>

					{this.renderInput({
						type: 'email',
						name: 'email',
						label: 'Email Address',
						placeholder: 'e.g. jdoe@russelsmithgroup.com',
					})}

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'phone',
								label: 'Phone Number',
								placeholder: '(Optional)',
							})}
						</div>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'designation',
								label: 'Designation',
								placeholder: 'Enter designation',
							})}
						</div>

						<Conditional isVisible={userType === USER_TYPES.INTERNAL}>
							<div className="col">
								{this.renderMultiSelect({
									options: this.props.divisions,
									name: 'division',
									label: 'Division',
									placeholder: 'Select Division',
									showCheckbox: true,
									loading: false,
								})}
							</div>
						</Conditional>
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'roleId',
							label: 'Role',
							placeholder: 'Select Role',
							options: this.getRoles(),
						})}
					</div>

					<div className="row">
						<Conditional isVisible={userType === 'external user'}>
							<div className="col">
								{this.renderSelect({
									name: 'organisationId',
									label: 'Organization ID',
									placeholder: 'Select Organization',
									options: this.props.organizationsForDropdown,
								})}
							</div>
						</Conditional>

						<Conditional isVisible={userType === 'external user'}>
							<div className="col">
								{this.renderSelect({
									name: 'facilityId',
									label: 'Facility ID',
									placeholder: 'Select Facility',
									options: this.getOrganizationFacilities(),
								})}
							</div>
						</Conditional>
					</div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Create')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => {
	return {
		divisions: state.divisions.list.map(utils.transformDivisionItem),
	};
};

const mapDispatchToProps = (dispatch: any) => {
	return {
		loadDivisions: () => dispatch(loadDivisions()),
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withRoles(withOrganizations(CreateUser)));
